import { ArrowBack } from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { useClaimQuery } from '../../../api/gql';
import { ExpandedBox } from '../../../components/ExpandedBox';
import { FullSizeLoader } from '../../../components/FullSizeLoader';
import { InvoiceControlBox } from '../../../components/invoice/InvoiceControlBox';
import { InvoiceScheduledActionNote } from '../../../components/invoice/InvoiceScheduledActionNote';
import { PageHeader } from '../../../components/PageHeader';
import { useSafeNavigation } from '../../../hooks/useSafeNavigation';
import { PageContent, PageLayout } from '../../../layout';
import { ClaimDataOverview } from '../../../modules/claims/claim/ClaimDataOverview';
import { useClaimDataOverview } from '../../../modules/claims/claim/data-overview/useClaimDataOverview';
import { CustomerContactForm } from '../../../modules/forms/customer/CustomerContactForm';
import { InvoiceArchiveButton } from '../../../modules/invoice/InvoiceArchiveButton';
import { ScenarioInfoBox } from '../../../modules/scenarios/ScenarioDetailOverview';
import { notify } from '../../../utils/notification';
import { ScheduledActionsTableBox } from './modules/InvoiceScheduledActionsBox';

const TRANS_NS = 'page-claim-detail';

export const ClaimDetail = () => {
  const { invoiceId } = useParams();
  const { t } = useTranslation(TRANS_NS);
  const { safeNavigate } = useSafeNavigation();

  useClaimDataOverview({ id: invoiceId ?? '', transNS: TRANS_NS });

  const { data, isLoading, isError } = useClaimQuery({
    id: invoiceId ?? '',
  });

  const hasBONote = useMemo(() => data?.invoice.scheduledActions.some((a) => a.note), [data?.invoice.scheduledActions]);

  const onArchived = (failed?: string[]) => {
    if (failed?.length) {
      notify.negative({ message: t('archive.failed') });
      return;
    }

    notify.positive({ message: t('archive.success') });
    safeNavigate('/claims');
  };
  const onArchiveError = () => {
    notify.negative({ message: t('archive.failed') });
  };

  if (isLoading) {
    return <FullSizeLoader />;
  }

  return (
    <>
      <PageLayout>
        <PageHeader
          title={`${t('title')}`}
          subtitle={data?.invoice.number}
          Icon={ArrowBack}
          action={
            invoiceId ? (
              <Stack direction={'row'} gap={2} alignItems={'center'}>
                {data?.invoice.canBeArchived ? (
                  <InvoiceArchiveButton invoiceIds={[invoiceId]} onArchived={onArchived} onError={onArchiveError} />
                ) : null}
              </Stack>
            ) : null
          }
        />
        <PageContent
          sx={{
            flexDirection: 'column',
            gap: 4,
            justifyContent: 'flex-start',
          }}
        >
          {data?.invoice ? <InvoiceControlBox invoice={data.invoice} transNS={TRANS_NS} /> : null}
          <Box width={'100%'} display={'flex'} flexWrap={'wrap'} gap={2}>
            <Box flex={1} width={'100%'}>
              <Typography variant={'h3'} mb={2}>
                {t('scheduledActions.title')}
              </Typography>
              <ScheduledActionsTableBox
                scheduledActions={data?.invoice.scheduledActions}
                isLoading={isLoading}
                isError={isError}
                transNS={TRANS_NS}
              />
            </Box>
            {hasBONote ? (
              <Box flex={1}>
                <Typography variant={'h3'} mb={2}>
                  {t('operatorNotes')}
                </Typography>
                <Stack gap={1}>
                  {data?.invoice.scheduledActions.map((scheduledAction) => (
                    <InvoiceScheduledActionNote key={scheduledAction.id} scheduledAction={scheduledAction} />
                  ))}
                </Stack>
              </Box>
            ) : null}
          </Box>
          <Box width={'100%'} display={'flex'} flexWrap={'wrap'} gap={2}>
            <Box flex={2}>
              <Typography variant={'h3'} mb={2}>
                {t('invoiceInfo')}
              </Typography>

              <Paper variant={'outlined'} sx={{ flex: 1, p: 4, position: 'relative' }}>
                {isLoading ? <FullSizeLoader /> : null}
                {invoiceId ? <ClaimDataOverview id={invoiceId} /> : null}
                {isError ? (
                  <ExpandedBox>
                    <Typography color={'text.secondary'}>{t('error.query')}</Typography>
                  </ExpandedBox>
                ) : null}
              </Paper>
            </Box>
            <Box flex={1}>
              <Typography variant={'h3'} mb={2}>
                {t('customerContact')}
              </Typography>
              <Paper sx={{ p: 4, position: 'relative', minHeight: 175 }}>
                {data?.invoice.customer && invoiceId ? (
                  <CustomerContactForm
                    invoiceId={invoiceId}
                    id={data.invoice.customer.id}
                    vatId={data.invoice.customer.vatId}
                    name={data.invoice.customer.name}
                    email={data.invoice.customer.email}
                    phoneNumber={data.invoice.customer.phoneNumber}
                  />
                ) : null}
                {isError ? (
                  <ExpandedBox>
                    <Typography color={'text.secondary'}>{t('error.query')}</Typography>
                  </ExpandedBox>
                ) : null}
              </Paper>
            </Box>
          </Box>
          {data?.invoice.scenario ? (
            <Box flex={1}>
              <Typography variant={'h3'} mb={2}>
                {t('scenario')}
              </Typography>
              <ScenarioInfoBox scenarioId={data.invoice.scenario.id} />
            </Box>
          ) : null}
        </PageContent>
      </PageLayout>
      {/*OUTLET for modal children */}
      <Outlet />
    </>
  );
};
